import Layout from '../../components/layout';
import { Theme } from '@lucid/gatsby-theme-lucid';

import { graphql } from 'gatsby';
import { Icon, IconType, Heading } from 'lucid-components-react';
import React, { useState, FC } from 'react';
import Select, {
  components,
  Styles,
  ValueType,
  SelectComponentsConfig
} from 'react-select';

import ReactHtmlParser from 'react-html-parser';

import Footer from '../../components/footer/footer';
import Navbar from '../../components/navbar/navbar';
import {
  Drupal_NodeLearningCenter,
  Drupal_ParagraphLink,
  Drupal_Entity,
  Maybe,
  Drupal_TaxonomyTermDiscoveryType,
  Drupal_ParagraphListOfLinks,
  Drupal_TaxonomyTermPersonas
} from '../../generated/graphql';
import { useMenuFooter, useMenuMainNavLinks } from '../../hooks/use-menu';
import { TemplateProps } from '../../types/types';
import { getEntityFields } from '../../utils/getEntityFields';
import Link from '../../components/link/link';
import TemplateCard from '../../components/template-card/template-card';
import LucidContainer from '../../components/lucid-container/lucid-container';
import styled from '../../theme/styled';
import CardTabs from '../../components/card-tabs/card-tabs';
import PaginatorNew from '../../components/paginator-new/paginator-new';
import { lucidColors, getLinkPath, i18nFunct } from '@lucid/gatsby-theme-lucid';
import ListOfLinks from '../../components/list-of-links/list-of-links';
import { useTheme } from 'emotion-theming';
import SignUpCta from '../../components/sign-up-cta/sign-up-cta';

interface OptionType {
  [key: string]: string;
}

const templateGridRowGutterRems = 3;
const templateGridColumnGutterRems = 2.5;

const SectionIntro = styled.p({
  maxWidth: '42.5rem',
  marginBottom: '6rem'
});

const GrayContainer = styled.div({
  backgroundSize: 'cover',
  backgroundImage:
    'url("https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/background.svg")',
  paddingBottom: '4rem'
});

const MagnifyingGlass = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    width="1em"
    height="1em"
    style={{
      marginLeft: '0.5rem',
      marginRight: '1rem',
      verticalAlign: 'text-top'
    }}
    preserveAspectRatio="xMidYMid meet"
    viewBox="0 0 512 512"
  >
    <path d="M414 354q-18-18-41-11l-32-32q43-53 43-119q0-80-56-136T192 0T56 56T0 192t56 136t136 56q70 0 119-43l32 32q-6 24 11 41l85 85q13 13 30 13q18 0 30-13q13-13 13-30t-13-30zm-222-13q-62 0-105.5-43.5T43 192T86.5 86.5T192 43t105.5 43.5T341 192t-43.5 105.5T192 341z" />
  </svg>
);

const DropdownIndicator: SelectComponentsConfig<
  OptionType
>['DropdownIndicator'] = ({ innerProps, selectProps: { menuIsOpen } }) => (
  <button
    {...innerProps}
    tabIndex={-1}
    css={theme => ({
      display: 'inline-block',
      fontSize: '100%',
      width: '3.5rem',
      height: '100%',
      textAlign: 'center',
      transition: 'backgroundColor .5s',
      backgroundColor: menuIsOpen ? theme.colors.primary : 'transparent',
      border: 0,
      borderRadius: '0 3px 3px 0',
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: menuIsOpen
          ? theme.colors.primary
          : lucidColors.coolGrey[10]
      }
    })}
  >
    <Icon
      type={IconType.CarotDown}
      css={theme => ({
        top: '3px',
        transition: 'transform .5s',
        transform: menuIsOpen ? 'rotate(-180deg)' : 'rotate(0deg)',
        color: menuIsOpen ? 'white' : theme.colors.primary
      })}
    />
  </button>
);

const Input: SelectComponentsConfig<OptionType>['Input'] = props => (
  <div>
    <span
      css={{
        position: 'absolute',
        fill: lucidColors.coolGrey[40],
        top: 'calc(50% - 10px)'
      }}
    >
      <MagnifyingGlass />
    </span>
    <components.Input {...props} />
  </div>
);

const outlineColor = lucidColors.coolGrey[10];
const outlineColorActive = lucidColors.coolGrey[30];
const placeholderWidth = '2.5rem';

const byTaxonomy = (taxonomy: string) => (entity: Maybe<Drupal_Entity>) =>
  (entity as Drupal_Entity).entityBundle === taxonomy;

const hasLinks = (entity: Maybe<Drupal_Entity>) =>
  (entity?.entityTranslation as Drupal_TaxonomyTermDiscoveryType).fieldLinks
    ?.length! > 0;

interface LearningCenterSelectProps {
  options: OptionType[];
  handleOnChange: (selectedOption: ValueType<OptionType>) => void;
  chosenDiagramType: ValueType<OptionType>;
  placeholder: string;
}

const LearningCenterSelect: FC<LearningCenterSelectProps> = ({
  options,
  handleOnChange,
  chosenDiagramType,
  placeholder
}) => {
  const theme = useTheme<Theme>();

  const reactSelectStyles: Styles = {
    clearIndicator: provided => ({
      ...provided,
      fontSize: '100%',
      height: '100%',
      width: '3.5rem',
      display: 'flex',
      borderLeft: `1px solid ${outlineColor}`,
      color: theme.colors.text,

      '&:hover': {
        cursor: 'pointer',
        background: lucidColors.coolGrey[10],
        color: outlineColorActive
      },

      '&:active': {
        background: lucidColors.coolGrey[10],
        color: outlineColorActive
      },

      '& > svg': {
        margin: 'auto',
        transform: 'scale(1.25)'
      }
    }),

    container: provided => ({
      ...provided,
      maxWidth: '480px'
    }),

    control: (provided, state) => ({
      ...provided,
      height: '3rem',
      borderRadius: '5px',
      borderColor: state.isFocused ? theme.colors.primary : outlineColor,
      boxShadow: state.isFocused
        ? `0 0 1px ${theme.colors.primary}`
        : provided.boxShadow,

      '&:hover': {
        borderColor: state.isFocused
          ? theme.colors.primary
          : lucidColors.coolGrey[30]
      }
    }),

    indicatorSeparator: provided => ({
      ...provided,
      margin: 0,
      backgroundColor: outlineColor
    }),

    input: provided => ({
      ...provided,
      marginLeft: placeholderWidth,
      color: theme.colors.text
    }),

    menu: provided => ({
      ...provided,
      backgroundColor: lucidColors.coolGrey[5],
      padding: '1rem',
      width: '780px',
      [`@media screen and (max-width: ${theme.breakpoints[4]})`]: {
        width: '100%'
      }
    }),

    menuList: provided => ({
      ...provided,
      display: 'grid',
      maxHeight: '100%',
      gridTemplateColumns: 'auto auto auto auto',
      [`@media screen and (max-width: ${theme.breakpoints[4]})`]: {
        gridTemplateColumns: 'auto auto'
      },
      [`@media screen and (max-width: ${theme.breakpoints[1]})`]: {
        gridTemplateColumns: 'auto'
      }
    }),

    option: (provided, state) => ({
      ...provided,
      flex: '0 1 23%',
      fontSize: '1rem',
      background: 'transparent',
      color:
        state.isSelected | state.isFocused
          ? theme.colors.primary
          : lucidColors.coolGrey[60],
      textDecoration: state.isSelected ? 'underline' : 'none',
      '&:active': {
        background: 'transparent'
      },

      '&:hover': {
        color: theme.colors.primary,
        cursor: 'pointer'
      }
    }),

    placeholder: provided => ({
      ...provided,
      marginLeft: placeholderWidth
    }),

    singleValue: provided => ({
      ...provided,
      marginLeft: placeholderWidth,
      color: theme.colors.text
    }),

    valueContainer: provided => ({
      ...provided,
      '&:hover': {
        cursor: 'text'
      }
    })
  };

  return (
    <Select
      components={{ DropdownIndicator, Input }}
      isSearchable
      isClearable
      options={options}
      value={chosenDiagramType}
      onChange={handleOnChange}
      styles={reactSelectStyles}
      placeholder={placeholder}
      css={{ marginBottom: '2.5rem' }}
    />
  );
};

const LearningCenter = ({ data, pageContext, location }: TemplateProps) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeLearningCenter;
  const {
    fieldTitle,
    fieldTitlePiece,
    fieldSubTitle,
    fieldHeroImage,
    fieldDiagramTypeTitle,
    fieldDiagramTypeBody,
    fieldRolesTitle,
    fieldRolesBody,
    fieldTemplatesTitle,
    fieldTemplateCards,
    fieldTemplateSectionLink
  } = node;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const { langPrefix, pageType, nid, hideSsr, i18nKey } = pageContext;
  const navbarMenu = useMenuMainNavLinks(langPrefix);
  const footerMenu = useMenuFooter(langPrefix);
  const [chosenDiagramType, setChosenDiagramType] = useState<OptionType | null>(
    null
  );

  const i18n = i18nFunct(i18nData, i18nKey);

  const fullTitle = fieldTitlePiece?.length
    ? fieldTitlePiece.filter(piece => piece?.trim()).join('<br>')
    : fieldTitle;

  const handleOnChange = (selectedOption: ValueType<OptionType>) =>
    setChosenDiagramType(selectedOption as OptionType);

  const tabs = (drupal.taxonomyTermQuery?.entities || [])
    .filter(byTaxonomy('discovery_type'))
    .filter(hasLinks)
    .filter(
      entity =>
        !chosenDiagramType || entity?.entityId === chosenDiagramType?.value
    )
    .map(entity => {
      const entityTranslation = entity?.entityTranslation as Drupal_TaxonomyTermDiscoveryType;
      const primaryLink = entityTranslation.fieldPrimaryLink?.entity
        ?.entityTranslation as Drupal_ParagraphLink;

      const listsOfLinks = entityTranslation.fieldLinks?.map(field => {
        const entity = field?.entity
          ?.entityTranslation as Drupal_ParagraphListOfLinks;
        return entity ? (
          <ListOfLinks
            key={entity?.entityId!}
            columnSize={7}
            css={{ flex: 'auto' }}
            entity={entity}
          />
        ) : null;
      });

      return {
        key: entityTranslation.entityId!,
        header: entityTranslation.entityLabel!,
        link: getLinkPath(primaryLink?.fieldCta?.url, langPrefix) || '',
        linkText: primaryLink?.fieldCta?.title || '',
        children: listsOfLinks
      };
    });

  const personaTabs = (drupal.taxonomyTermQuery?.entities || [])
    .filter(byTaxonomy('personas'))
    .map(entity => {
      const entityTranslation = entity?.entityTranslation as Drupal_TaxonomyTermPersonas;
      const listsOfLinks = entityTranslation.fieldLinks?.map(field => {
        const entity = field?.entity
          ?.entityTranslation as Drupal_ParagraphListOfLinks;
        return (
          <ListOfLinks key={entity?.entityId!} columnSize={7} entity={entity} />
        );
      });

      return {
        key: entityTranslation.entityId!,
        header: entityTranslation.entityLabel!,
        children: listsOfLinks
      };
    });

  const options: OptionType[] = (drupal.taxonomyTermQuery?.entities || [])
    .filter(byTaxonomy('discovery_type'))
    .filter(hasLinks)
    .map(entity => {
      const entityTranslation = entity?.entityTranslation as Drupal_Entity;
      return {
        value: entityTranslation.entityId!,
        label: entityTranslation.entityLabel!
      };
    });

  return (
    <Layout
      pageType={pageType}
      location={location}
      langPrefix={langPrefix}
      entityTranslations={entityTranslations}
      metatags={entityMetatags}
      nid={nid}
      hideSsr={hideSsr}
    >
      <Navbar menu={navbarMenu} />
      <main css={{ fontSize: '1.25rem', paddingTop: '4rem' }}>
        <LucidContainer
          as="header"
          css={theme => ({
            display: ['-ms-grid', 'grid'],
            msGridColumns: '1fr 1.25rem 1fr',
            gridTemplateColumns: '1fr 1fr',
            gridColumnGap: '1.25rem',
            minHeight: '400px',
            [`@media screen and (max-width: ${theme.breakpoints[4]})`]: {
              msGridColumns: '80%',
              gridTemplateColumns: '80%'
            },
            [`@media screen and (max-width: ${theme.breakpoints[1]})`]: {
              msGridColumns: '100%',
              gridTemplateColumns: '100%'
            }
          })}
        >
          <div
            css={theme => ({
              msGridColumn: 1,
              [`@media screen and (max-width: ${theme.breakpoints[4]})`]: {
                flex: '0 1 580px'
              }
            })}
          >
            <Heading variant="headlineM" mb={5}>
              {fullTitle && ReactHtmlParser(fullTitle)}
            </Heading>
            <p
              css={{
                fontSize: '1.875rem',
                lineHeight: 1.5,
                fontWeight: 300,
                marginBottom: '3rem'
              }}
            >
              {fieldSubTitle}
            </p>
            <SignUpCta />
          </div>
          <div
            css={theme => ({
              msGridColumn: 3,
              [`@media screen and (max-width: ${theme.breakpoints[4]})`]: {
                display: 'none'
              }
            })}
          >
            <img src={fieldHeroImage || ''} alt="Learning Center" />
          </div>
        </LucidContainer>

        <GrayContainer>
          <LucidContainer as="section" css={{ marginBottom: '6rem' }}>
            <Heading as="h2" variant="subheadM" mt={4}>
              {fieldDiagramTypeTitle}
            </Heading>
            <SectionIntro css={{ marginBottom: '5rem' }}>
              {fieldDiagramTypeBody}
            </SectionIntro>
            <LearningCenterSelect
              options={options}
              handleOnChange={handleOnChange}
              chosenDiagramType={chosenDiagramType}
              placeholder={i18n('mktg-diagram-type')}
            />
            <PaginatorNew
              items={tabs}
              pageSize={6}
              alwaysRender
              render={visibleTabs => (
                <CardTabs
                  className="diagram-types"
                  tabs={visibleTabs}
                  css={{ marginBottom: '1.75rem' }}
                />
              )}
            />
          </LucidContainer>

          <LucidContainer as="section">
            <Heading as="h2" variant="subheadM" mt={4}>
              {fieldRolesTitle}
            </Heading>
            <SectionIntro>{fieldRolesBody}</SectionIntro>

            <CardTabs className="persona-cards" tabs={personaTabs} />
          </LucidContainer>
        </GrayContainer>

        <div
          css={{
            paddingBottom: '5.25rem',
            backgroundColor: lucidColors.indigo[105]
          }}
        >
          <LucidContainer as="section">
            <Heading
              as="h2"
              variant="subheadM"
              display="inline-block"
              my={8}
              color="white"
            >
              {fieldTemplatesTitle}
            </Heading>

            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                marginBottom: '4rem'
              }}
            >
              {fieldTemplateCards?.map(
                (card, i) =>
                  card?.entity && (
                    <TemplateCard
                      css={theme => ({
                        flex: `0 1 calc(33% - ${(templateGridRowGutterRems *
                          2) /
                          3}rem)`,
                        flexBasis: `calc(33% - ${(templateGridRowGutterRems *
                          2) /
                          3}rem)`,
                        marginBottom: `${templateGridColumnGutterRems}rem`,

                        [`@media screen and (max-width: ${theme.breakpoints[4]})`]: {
                          flex: `0 1 calc(50% - ${templateGridRowGutterRems /
                            2}rem)`,
                          flexBasis: `calc(50% - ${templateGridRowGutterRems /
                            2}rem)`
                        },

                        [`@media screen and (max-width: ${theme.breakpoints[1]})`]: {
                          flex: '0 1 100%',
                          flexBasis: '100%'
                        }
                      })}
                      key={i}
                      entity={card.entity}
                    />
                  )
              )}
            </div>

            <div css={{ textAlign: 'right' }}>
              <Link
                to={getLinkPath(fieldTemplateSectionLink?.url, langPrefix)}
                inverse
                hasArrow
              >
                {fieldTemplateSectionLink?.title}
              </Link>
            </div>
          </LucidContainer>
        </div>
      </main>
      <Footer menu={footerMenu} entityTranslations={entityTranslations} />
    </Layout>
  );
};

export default LearningCenter;

export const query = graphql`
  query(
    $nid: String!
    $langPrefix: [String!]
    $LanguageId: Drupal_LanguageId!
    $i18nKey: String!
  ) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeLearningCenter {
          ...entityFields
          fieldExtraTranslations {
            entity {
              ...extraTranslations
            }
          }
          fieldTitle
          fieldTitlePiece
          fieldSubTitle
          fieldHeroImage
          fieldDiagramTypeTitle
          fieldDiagramTypeBody
          fieldRolesTitle
          fieldRolesBody
          fieldTemplatesTitle
          fieldTemplateCards {
            entity {
              ... on Drupal_ParagraphConsiderationCards {
                fieldAltText
                fieldCardHeader
                fieldImage
                fieldLink
              }
            }
          }
          fieldTemplateSectionLink {
            title
            url {
              ...getLinkPath
            }
          }
        }
      }

      nodeQuery(
        limit: 1000
        filter: {
          conditions: [
            {
              field: "type"
              operator: IN
              value: [
                "consideration"
                "discovery"
                "nineteen-solutions"
                "roles-hub"
                "case_study"
              ]
            }
            { field: "status", value: "1", operator: EQUAL }
            { field: "langcode", value: $langPrefix, operator: EQUAL }
          ]
          groups: [
            {
              conjunction: OR
              conditions: [
                { field: "field_is_private", value: "false", operator: EQUAL }
                { field: "field_is_private", operator: IS_NULL }
              ]
            }
          ]
        }
        sort: [
          { field: "sticky", direction: DESC }
          { field: "created", direction: DESC }
        ]
      ) {
        entities {
          entityBundle
          entityTranslation(language: $LanguageId) {
            entityId
            entityBundle
            entityUrl {
              path
            }
            ... on Drupal_Node {
              title
              sticky
              created
            }

            ... on Drupal_NodeConsideration {
              fieldConsiderationTitle
              fieldConsiderationTitlePiece
              fieldDiscoveryType {
                targetId
              }
            }
          }
        }
      }

      taxonomyTermQuery(
        filter: {
          conditions: [
            {
              field: "vid"
              value: ["discovery_type", "personas"]
              operator: IN
            }
            { field: "langcode", value: $langPrefix, operator: EQUAL }
          ]
        }
        limit: 10000
      ) {
        entities {
          entityId
          entityBundle
          entityTranslation(language: $LanguageId) {
            entityId
            entityLabel

            ... on Drupal_TaxonomyTermDiscoveryType {
              fieldLinks {
                entity {
                  entityId
                  entityTranslation(language: $LanguageId) {
                    ...listOfLinks
                  }
                }
              }
              fieldPrimaryLink {
                entity {
                  entityTranslation(language: $LanguageId) {
                    ... on Drupal_ParagraphLink {
                      fieldCta {
                        title
                        url {
                          ...getLinkPath
                        }
                      }
                    }
                  }
                }
              }
            }

            ... on Drupal_TaxonomyTermPersonas {
              fieldLinks {
                entity {
                  entityId
                  entityTranslation(language: $LanguageId) {
                    ...listOfLinks
                  }
                }
              }
            }
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_diagram_type
          }
        }
      }
    }
  }
`;
